import dayjs from 'dayjs';
import _ from 'lodash';

import { ActorType } from 'constants/aiAttribution';
import { UUID_PATTERN } from 'constants/general';
import { LoanChangesHistoryEntityType } from 'constants/loanChanges';
import {
  HistoryPendingChangesActionType,
  HistoryTypeToPendingChangesTypeMap,
  PendingChangeKind,
} from 'constants/pendingChanges';
import {
  AI_ASSISTANT_AUTHOR_NAME,
  AI_AUTO_INITIATED_LABEL,
  AI_PENDING_CHANGES_AUTHOR_KEY,
  AI_STARTED_BY_PREFIX,
  AI_USER_STARTED_TRIGGER_SOURCES,
  AiActorTypeName,
  PendingChangesAuthorsHeaderToken,
} from 'constants/pendingChangesAuthors';
import { getColorByIndex } from 'constants/presence';
import { getPersonFullName } from 'utils/loanApplication';
import { resolvePendingChangePropertyKey } from 'utils/pendingChanges';

const getTimestamp = date => (_.isNil(date) ? 0 : dayjs(date).valueOf());

export const checkIsAiPendingChange = ({ actorType } = {}) =>
  actorType === ActorType.AI_PROCESS || actorType === AiActorTypeName.AI_PROCESS;

export const getPendingChangeAuthorKey = change => {
  if (checkIsAiPendingChange(change)) {
    return AI_PENDING_CHANGES_AUTHOR_KEY;
  }

  return change?.createdByUser?.id ?? null;
};

const getPendingChangeFieldKey = change =>
  `${change.entityPrimaryKey}:${resolvePendingChangePropertyKey(change)}`;

// A field someone edited is a change even if a later save recalculated it; history recorded before
// change kinds existed has none and counts as changes.
const checkIsRecalculatedField = fieldChanges =>
  fieldChanges.every(({ changeKind }) => changeKind === PendingChangeKind.RECALCULATED);

// A user's change count is the fields they directly changed — recalculated (affected) values follow
// the change that caused them and are not counted (matches the save dialog's direct-change count).
const countDirectChangeFields = changes =>
  Object.values(_.groupBy(changes, getPendingChangeFieldKey)).filter(
    fieldChanges => !checkIsRecalculatedField(fieldChanges),
  ).length;

export const flattenPendingChanges = changesByEntity => Object.values(changesByEntity || {}).flat();

export const annotatePendingChanges = changes =>
  (changes || []).map(change => ({
    ...change,
    authorKey: getPendingChangeAuthorKey(change),
    fieldKey: getPendingChangeFieldKey(change),
  }));

const getPendingChangeDisplayValue = (value, valueDetails) => valueDetails ?? value ?? null;

/**
 * Adds a history entry's author to a field's author list: one row per author, keeping the author's
 * first old value and latest new value. Expects entries in chronological order.
 */
export const appendPendingChangeAuthor = (authors = [], change) => {
  const key = getPendingChangeAuthorKey(change);
  const existingAuthor = authors.find(author => author.key === key);
  const isAi = key === AI_PENDING_CHANGES_AUTHOR_KEY;
  const createdByUserName = getPersonFullName(change.createdByUser || {}) || null;

  return [
    ...authors.filter(author => author.key !== key),
    {
      key,
      name: isAi ? AI_ASSISTANT_AUTHOR_NAME : createdByUserName,
      starterName: isAi ? createdByUserName : null,
      aiProcessExecutionId: change.aiProcessExecutionId ?? null,
      createdDate: change.createdDate ?? null,
      oldValue: existingAuthor
        ? existingAuthor.oldValue
        : getPendingChangeDisplayValue(change.oldValue, change.oldValueDetails),
      newValue: getPendingChangeDisplayValue(change.newValue, change.newValueDetails),
    },
  ];
};

/**
 * Per-field author rows for one entity's pending changes, keyed the way the edit forms key a field (or by
 * getFieldKey), so a read-only view can show the same author dots as the edit drawers. Only the given
 * authors' changes count.
 */
export const buildFieldPendingChangeAuthors = (
  changes,
  authorKeys,
  getFieldKey = resolvePendingChangePropertyKey,
) =>
  _.orderBy(
    (changes || []).filter(change => authorKeys.includes(getPendingChangeAuthorKey(change))),
    [({ createdDate }) => getTimestamp(createdDate)],
  ).reduce((acc, change) => {
    const fieldKey = getFieldKey(change);

    acc[fieldKey] = appendPendingChangeAuthor(acc[fieldKey], change);

    return acc;
  }, {});

// A view can show fields of several entities at once (e.g. the loan and its application); an author who
// changed the same field on more than one keeps a single row, the later entity's.
export const mergeFieldPendingChangeAuthors = fieldAuthorsList =>
  fieldAuthorsList.reduce(
    (acc, fieldAuthors) =>
      _.mergeWith(acc, fieldAuthors, (existingAuthors, authors) =>
        existingAuthors ? _.uniqBy([...authors, ...existingAuthors], 'key') : authors,
      ),
    {},
  );

/**
 * A field's author rows by its name as a view knows it: the edit-form key, or the PascalCase name the
 * preview DTOs list in modifiedFields. Null when nobody selected changed it.
 */
export const getFieldPendingChangeAuthors = (fieldAuthors, fieldName) =>
  _.isNil(fieldName) || _.isEmpty(fieldAuthors)
    ? null
    : (fieldAuthors[fieldName] ?? fieldAuthors[_.camelCase(fieldName)] ?? null);

/**
 * Author rows for a value a read-only view builds from several fields (an address, a combined amount): an
 * author who changed more than one of them keeps a single row. Null when nobody selected changed any.
 */
export const getFieldsPendingChangeAuthors = (fieldAuthors, fieldNames = []) => {
  const authors = _.uniqBy(
    fieldNames.flatMap(fieldName => getFieldPendingChangeAuthors(fieldAuthors, fieldName) ?? []),
    'key',
  );

  return _.isEmpty(authors) ? null : authors;
};

// The author rows of one change the backend lists behind a preview field: a property's rows, or every row of the
// entity when the change has no property (e.g. an owner link added or removed). The backend names the history
// property as recorded, so the lookup is keyed by the raw history propertyName (see
// selectPropertyPendingChangeAuthorsByEntityKey), not by the edit forms' owned-entity key.
const getModifiedFieldChangeAuthors = (
  propertyAuthorsByEntityKey,
  { entityPrimaryKey, propertyName },
) => {
  const entityPropertyAuthors = propertyAuthorsByEntityKey?.[entityPrimaryKey];

  if (_.isEmpty(entityPropertyAuthors)) {
    return [];
  }

  return _.isNil(propertyName)
    ? Object.values(entityPropertyAuthors).flat()
    : (entityPropertyAuthors[_.camelCase(propertyName)] ?? []);
};

/**
 * Author rows of preview-DTO fields, from the change-history properties the backend lists behind each field
 * (modifiedFieldDetails[].changes) — so a field shown from several properties (a full name, an address) needs no
 * frontend map of its sources. An author who changed more than one of them keeps a single row. Null when nobody
 * selected changed them.
 */
export const getModifiedFieldDetailsPendingChangeAuthors = (
  propertyAuthorsByEntityKey,
  modifiedFieldDetails,
  fieldNames,
) => {
  const fieldNameKeys = _.castArray(fieldNames).map(fieldName => _.camelCase(fieldName));
  const authors = _.uniqBy(
    (modifiedFieldDetails || [])
      .filter(({ fieldName }) => fieldNameKeys.includes(_.camelCase(fieldName)))
      .flatMap(({ changes }) => changes || [])
      .flatMap(change => getModifiedFieldChangeAuthors(propertyAuthorsByEntityKey, change)),
    'key',
  );

  return _.isEmpty(authors) ? null : authors;
};

export const buildPendingChangesAuthorsHeaderValue = authorKeys =>
  _.isEmpty(authorKeys) ? PendingChangesAuthorsHeaderToken.NONE : authorKeys.join(',');

export const buildPendingChangesAuthorsRequest = authorKeys => ({
  userIds: (authorKeys || []).filter(key => key !== AI_PENDING_CHANGES_AUTHOR_KEY),
  includeAiChanges: (authorKeys || []).includes(AI_PENDING_CHANGES_AUTHOR_KEY),
});

export const buildAuthorColorMap = (authors, getAuthorKey = author => author.formattedFullName) =>
  authors.reduce((acc, author, index) => {
    const colorSet = getColorByIndex(index);

    return { ...acc, [getAuthorKey(author)]: colorSet?.bg || null };
  }, {});

const resolveRunStarterName = run => {
  const name = run?.triggeredByUserName;

  return _.isNil(name) || name === '' || UUID_PATTERN.test(name) ? null : name;
};

/**
 * Describes who started the AI process run behind an AI pending change. A run without loaded details
 * falls back to the change's own author (the seam until every run is reachable), otherwise a manual or
 * loan-action run reads "started by <name>" and any automated source reads "Auto-initiated".
 */
export const getAiRunStarterLabel = (run, fallbackStarterName = null) => {
  if (_.isNil(run)) {
    return fallbackStarterName
      ? `${AI_STARTED_BY_PREFIX} ${fallbackStarterName}`
      : AI_AUTO_INITIATED_LABEL;
  }

  const source = String(run.triggerSource ?? '').toLowerCase();

  if (!AI_USER_STARTED_TRIGGER_SOURCES.includes(source)) {
    return AI_AUTO_INITIATED_LABEL;
  }

  const starterName = resolveRunStarterName(run) ?? fallbackStarterName;

  return starterName ? `${AI_STARTED_BY_PREFIX} ${starterName}` : AI_AUTO_INITIATED_LABEL;
};

const getAiStarterLabel = (aiChanges, aiRunsByExecutionId) =>
  _.uniq(
    _.orderBy(aiChanges, [({ createdDate }) => getTimestamp(createdDate)], ['desc']).map(change =>
      getAiRunStarterLabel(
        aiRunsByExecutionId?.[change.aiProcessExecutionId],
        getPersonFullName(change.createdByUser || {}) || null,
      ),
    ),
  ).join(', ');

const buildAuthorEntry = ({ key, authorChanges, assignedUsers, aiRunsByExecutionId }) => {
  const isAi = key === AI_PENDING_CHANGES_AUTHOR_KEY;
  const sortedChanges = _.orderBy(authorChanges, [({ createdDate }) => getTimestamp(createdDate)]);
  const lastChange = _.last(sortedChanges);
  const assignedUser = assignedUsers?.find(({ userId }) => userId === key);

  return {
    key,
    isAi,
    name: isAi
      ? AI_ASSISTANT_AUTHOR_NAME
      : getPersonFullName(lastChange?.createdByUser || {}) || null,
    role: isAi ? null : (assignedUser?.roleName ?? null),
    changesCount: countDirectChangeFields(authorChanges),
    firstChangeDate: _.first(sortedChanges)?.createdDate ?? null,
    lastChangeDate: lastChange?.createdDate ?? null,
    starterLabel: isAi ? getAiStarterLabel(authorChanges, aiRunsByExecutionId) : null,
  };
};

/**
 * Lists the current user plus every author with pending changes on the loan, current user first and
 * the rest in order of their first change so a newcomer never reshuffles everyone else's color.
 */
export const buildPendingChangesAuthors = ({
  changes,
  currentUserId,
  currentUserName = null,
  assignedUsers = [],
  aiRunsByExecutionId = {},
}) => {
  const changesByAuthor = _.groupBy(
    (changes || []).filter(change => !_.isNil(getPendingChangeAuthorKey(change))),
    getPendingChangeAuthorKey,
  );

  const otherAuthors = _.orderBy(
    Object.entries(changesByAuthor)
      .filter(([key]) => key !== currentUserId)
      .map(([key, authorChanges]) =>
        buildAuthorEntry({ key, authorChanges, assignedUsers, aiRunsByExecutionId }),
      ),
    [({ firstChangeDate }) => getTimestamp(firstChangeDate)],
  );

  const currentUserEntry = {
    ...buildAuthorEntry({
      key: currentUserId,
      authorChanges: changesByAuthor[currentUserId] ?? [],
      assignedUsers,
      aiRunsByExecutionId,
    }),
    name: currentUserName,
    isCurrentUser: true,
  };

  const authors = [currentUserEntry, ...otherAuthors];
  const colorMap = buildAuthorColorMap(authors, ({ key }) => key);

  return authors.map(author => ({
    isCurrentUser: false,
    ...author,
    color: colorMap[author.key],
  }));
};

export const getPendingChangesAuthorLabel = author => {
  if (!author) {
    return '';
  }

  return author.isAi && author.starterLabel
    ? `${author.name} (${author.starterLabel})`
    : author.name || '';
};

const getEarliestOldAndLatestNew = fieldChanges => {
  const sorted = _.orderBy(fieldChanges, [({ modifiedOnRaw }) => modifiedOnRaw ?? 0]);

  return { ..._.last(sorted), oldValue: _.first(sorted).oldValue };
};

const AUTOMATIC_FEE_AMOUNT_PROPERTY = 'amount';

// Pending changes key LoanFee properties in camelCase (`amount`); compare loosely so casing cannot hide a fee.
const getChangePropertyKey = ({ fieldKey }) => _.camelCase(_.last(String(fieldKey).split(':')));

const checkIsLoanFeeField = ([change]) =>
  change?.rootEntityTypeName === LoanChangesHistoryEntityType.LOAN_FEE &&
  change?.entityTypeName === LoanChangesHistoryEntityType.LOAN_FEE;

/**
 * One line per fee the recalculation added, removed or re-priced, so the save summary can show them next to
 * the changes that caused them instead of among the recalculated values. A recalculated fee row whose amount
 * did not move (e.g. only its source changed) stays a recalculated value.
 */
const buildAutomaticFeeChanges = (feeFields, feeTypeNamesById) =>
  Object.entries(_.groupBy(feeFields.map(getEarliestOldAndLatestNew), 'entityPrimaryKey'))
    .map(([entityPrimaryKey, fields]) => {
      const amount = fields.find(
        field => getChangePropertyKey(field) === AUTOMATIC_FEE_AMOUNT_PROPERTY,
      );
      const { actionType } = fields[0];
      const [feeTypeId] = String(entityPrimaryKey).split('_');

      return {
        key: entityPrimaryKey,
        feeName: feeTypeNamesById?.[feeTypeId] ?? null,
        pendingChangesActionType: HistoryTypeToPendingChangesTypeMap[actionType],
        oldValue: amount?.oldValue ?? null,
        newValue: amount?.newValue ?? null,
        fieldKeys: fields.map(({ fieldKey }) => fieldKey),
        isShown:
          !!amount &&
          (actionType !== HistoryPendingChangesActionType.MODIFIED ||
            amount.oldValue !== amount.newValue),
      };
    })
    .filter(({ isShown, feeName }) => isShown && !!feeName);

/**
 * Splits displayed pending changes into what a save of `targetKeys` commits (the changes, the fees their
 * recalculation added or changed, and apart from them the other values they recalculated), which of the
 * changed fields another author also has pending, and how many changes stay pending per remaining author.
 */
export const buildPendingChangesSaveSummary = ({
  changes,
  targetKeys,
  authors,
  feeTypeNamesById = null,
}) => {
  const isTargetChange = change => targetKeys.includes(change.authorKey);
  const targetChanges = (changes || []).filter(isTargetChange);
  const remainingChanges = (changes || []).filter(change => !isTargetChange(change));
  const remainingByField = _.groupBy(remainingChanges, 'fieldKey');
  const authorsByKey = _.keyBy(authors, 'key');

  const [recalculatedFields, changedFields] = _.partition(
    Object.values(_.groupBy(targetChanges, 'fieldKey')),
    checkIsRecalculatedField,
  );
  const willBeSaved = changedFields.map(getEarliestOldAndLatestNew);
  const automaticFeeChanges = buildAutomaticFeeChanges(
    recalculatedFields.filter(checkIsLoanFeeField),
    feeTypeNamesById,
  );
  const automaticFeeFieldKeys = new Set(automaticFeeChanges.flatMap(({ fieldKeys }) => fieldKeys));
  const recalculatedWillBeSaved = recalculatedFields
    .map(getEarliestOldAndLatestNew)
    .filter(({ fieldKey }) => !automaticFeeFieldKeys.has(fieldKey));

  const alsoChangedBySomeoneElse = willBeSaved
    .filter(({ fieldKey }) => remainingByField[fieldKey])
    .map(change => ({
      fieldKey: change.fieldKey,
      propertyName: change.propertyName,
      otherChanges: Object.values(_.groupBy(remainingByField[change.fieldKey], 'authorKey')).map(
        authorChanges => {
          const latest = getEarliestOldAndLatestNew(authorChanges);

          return {
            authorKey: latest.authorKey,
            authorName: getPendingChangesAuthorLabel(authorsByKey[latest.authorKey]),
            newValue: latest.newValue,
          };
        },
      ),
    }));

  const staysPending = Object.entries(_.groupBy(remainingChanges, 'authorKey'))
    .filter(([authorKey]) => authorsByKey[authorKey])
    .map(([authorKey, authorChanges]) => ({
      ...authorsByKey[authorKey],
      changesCount: countDirectChangeFields(authorChanges),
    }));

  return {
    willBeSaved,
    automaticFeeChanges,
    recalculatedWillBeSaved,
    alsoChangedBySomeoneElse,
    staysPending,
  };
};
